import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import FeedbackModal from "../components/feedbackmodel.jsx";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLoginContext } from "../Contexts/LoginContext.js";
import api from "../services/api.js";
import UserIcon from "../components/userIcon.jsx";
import { Helmet } from "react-helmet";
// React Icons
import { FaUserFriends, FaEye, FaWallet, FaUsers, FaChevronRight, FaArrowRight } from "react-icons/fa";
import {
  GiCow,
  GiBuffaloHead,
  GiReceiveMoney,
  GiPayMoney,
} from "react-icons/gi";
import DashboardSkeleton from "../components/DashboardSkeleton.jsx";

// --- 1. Custom Image Icon Component ---
const ImageIcon = ({ src, alt, className }) => (
  <img
    src={src}
    alt={alt}
    className={`object-contain ${className || "w-6 h-6"}`}
    aria-hidden="true"
  />
);

// --- 2. Clean Action Card Component ---
const ActionCard = ({ to, icon, title, colorClass }) => (
  <Link 
    to={to} 
    className="bg-white p-3 rounded-2xl border border-gray-100 shadow-sm flex flex-col items-center justify-center text-center h-24 active:scale-95 transition-all duration-200"
  >
    {/* Circle Background behind icon */}
    <div className={`p-2.5 rounded-full bg-opacity-10 mb-2 ${colorClass.replace('text-', 'bg-')}`}>
      <div className={`text-xl ${colorClass}`}>{icon}</div>
    </div>
    <span className="text-xs font-semibold text-gray-600 leading-tight">{title}</span>
  </Link>
);

const SectionHeader = ({ title }) => (
  <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 ml-1">
    {title}
  </h2>
);

const DashBoard = () => {
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const { role, loggedIn, loading } = useLoginContext();
  const navigate=useNavigate();
  useEffect(() => {
    let timer;
    const checkFeedbackPrompt = async () => {
      try {
        const res = await api.get(`/prompt`);
        if (res.data?.showFeedback) setIsFeedbackOpen(true);
      } catch (error) {
        console.error(error);
      }
    };
    if (loggedIn) timer = setTimeout(() => checkFeedbackPrompt(), 3000);
    return () => clearTimeout(timer);
  }, [loggedIn]);

  const handleCloseFeedback = () => setIsFeedbackOpen(false);
  const handleSubmitFeedback = async ({ rating, feedback }) => {
    try {
      await api.post(`/api/feedback`, { rating, feedback });
      toast.success("Thanks!");
    } catch (error) {
      toast.error("Error");
    } finally {
      handleCloseFeedback();
    }
  };

  const getGroups = () => {
    // 1. People (Icons: FontAwesome)
    const people = role === 'admin' ? [
        { id: 'customers', title: 'Customers', icon: <FaUsers />, path: '/customers', color: 'text-blue-600' },
        { id: 'today', title: "Active Today", icon: <FaUserFriends />, path: '/todays-customers', color: 'text-indigo-600' }
    ] : [];

    // 2. Main Hero Item (Icon: Your PNG, inverted to white)
    const milkEntry = { 
        id: 'milk', 
        title: 'New Milk Entry', 
        subtitle: 'Tap to add collection',
        // Note: 'brightness-0 invert' makes the png white to match the blue bg
        icon: <ImageIcon src="./icons/milk-can.png" alt="Milk" className="w-10 h-10 brightness-0 invert" />, 
        path: '/entries/milk-entry' 
    };

    // 3. Sub Entries (Icons: Mixed PNGs and React Icons)
    const subEntries = [
        { 
            id: 'ghee', 
            title: 'Ghee Sale', 
            // Using your PNG
            icon: <ImageIcon src="./icons/gheeIcon.png" alt="Ghee" className="w-6 h-6" />, 
            path: '/entries/ghee-entry', 
            color: 'text-orange-500' // This creates a light orange circle bg
        },
        { 
            id: 'feed', 
            title: 'Cattle Feed', 
            // Using your PNG
            icon: <ImageIcon src="./icons/feed.png" alt="Feed" className="w-6 h-6" />, 
            path: '/entries/feed-entry', 
            color: 'text-lime-600' // This creates a light lime circle bg
        },
        { 
            id: 'receive', 
            title: 'Receive', 
            icon: <GiReceiveMoney />, 
            path: '/entries/receive-money', 
            color: 'text-emerald-600' 
        },
        { 
            id: 'pay', 
            title: 'Pay', 
            icon: <GiPayMoney />, 
            path: '/entries/give-money', 
            color: 'text-rose-500' 
        },
    ];

    // 4. Insights (Icons: FontAwesome)
    const insights = [
        { id: 'view', title: 'View Entries', icon: <FaEye />, path: '/view-entries', color: 'text-violet-600' },
        { id: 'balance', title: 'Wallet', icon: <FaWallet />, path: '/balance', color: 'text-teal-600' }
    ];

    // 5. Prices (Icons: React Icons)
    const prices = role!="associated user" ? [
        { id: 'cow', title: 'Cow Rates', icon: <GiCow />, path: '/price/cow_Milk_Prices' },
        { id: 'buffalo', title: 'Buffalo Rates', icon: <GiBuffaloHead />, path: '/price/buffalo_Milk_Prices' }
    ] : [];

    return { people, milkEntry, subEntries, insights, prices };
  };
  if (!loggedIn) navigate("/");
  if (loading) return <DashboardSkeleton />;
  


  const { people, milkEntry, subEntries, insights, prices } = getGroups();

  return (
    <div className="min-h-screen w-full bg-gray-50/50 pb-20 font-sans text-gray-900">
      <Helmet><title>Dashboard</title></Helmet>

      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 px-4 py-4 md:px-5 ">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 md:h-12 md:w-12  rounded-xl flex items-center justify-center ">
              <img src="/images/logo.png" alt="logo" />
            </div>
            <div>
              <h1 className="font-black text-lg md:text-xl text-gray-900 tracking-tight">DairyKhataBook</h1>
              <div className="flex items-center text-[10px] md:text-xs text-gray-500 uppercase tracking-widest">
                <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-1.5"></span>
                {role === "admin" ? "Admin Dashboard" : role === "associated user" ? "Associated User" : "User Dashboard"}
              </div>
            </div>
          </div>
          <div>
            <UserIcon role={role} />
          </div>
        </div>
      </div>

      <div className="p-5 space-y-8 max-w-lg mx-auto mt-2">

        {/* SECTION 1: PEOPLE (Admin Only) */}
        {people.length > 0 && (
            <div>
                <SectionHeader title="People" />
                <div className="grid grid-cols-2 gap-4">
                    {people.map(item => (
                        <Link key={item.id} to={item.path} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center space-x-3 active:bg-gray-50 transition-colors">
                            <div className={`p-2 rounded-lg bg-gray-50 ${item.color} text-lg`}>{item.icon}</div>
                            <span className="font-semibold text-sm text-gray-700">{item.title}</span>
                        </Link>
                    ))}
                </div>
            </div>
        )}

        {/* SECTION 2: CORE OPERATIONS (5-in-1 Group) */}
        {role!="associated user" && <div>
            <SectionHeader title="Daily Operations" />
            
            {/* The "Hero" Button (Milk) */}
            <Link to={milkEntry.path} className="group relative block mb-4">
                <div className="absolute inset-0 bg-indigo-600 rounded-3xl transform transition-transform group-active:scale-95 shadow-lg shadow-indigo-200"></div>
                <div className="relative p-6 flex items-center justify-between">
                    <div>
                        <h3 className="text-xl font-bold text-white mb-1">{milkEntry.title}</h3>
                        <p className="text-indigo-100 text-xs font-medium">{milkEntry.subtitle}</p>
                    </div>
                    {/* Glassmorphism effect behind the Milk Icon */}
                    <div className="bg-white/20 p-3 rounded-2xl backdrop-blur-sm">
                        {milkEntry.icon}
                    </div>
                </div>
            </Link>

            {/* Sub Entries (Ghee, Feed, Cash) */}
            <div className="grid grid-cols-4 gap-3">
                {subEntries.map(item => (
                    <ActionCard 
                        key={item.id} 
                        to={item.path} 
                        icon={item.icon} 
                        title={item.title} 
                        colorClass={item.color} 
                    />
                ))}
            </div>
        </div>}

        {/* SECTION 3: INSIGHTS (View & Wallet) */}
        <div>
             <SectionHeader title="Overview" />
             <div className="grid grid-cols-2 gap-4">
                {insights.map(item => (
                    <Link key={item.id} to={item.path} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex justify-between items-center active:bg-gray-50 transition-colors">
                        <div>
                            <span className="block text-xs text-gray-400 font-medium uppercase mb-1">Check</span>
                            <span className="font-bold text-gray-800">{item.title}</span>
                        </div>
                        <div className={`text-xl ${item.color}`}>{item.icon}</div>
                    </Link>
                ))}
             </div>
        </div>

        {/* SECTION 4: SETTINGS LIST (Bottom) */}
        {prices.length > 0 && (
            <div className="border-t border-gray-200 pt-6">
                 {prices.map(item => (
                    <Link key={item.id} to={item.path} className="flex items-center justify-between py-3 px-2 group hover:bg-gray-50 rounded-lg transition-colors">
                        <div className="flex items-center space-x-4">
                            <div className="text-gray-400 group-hover:text-indigo-600 transition-colors text-lg">{item.icon}</div>
                            <span className="font-medium text-sm text-gray-600 group-hover:text-gray-900">{item.title}</span>
                        </div>
                        <FaArrowRight className="text-gray-300 text-xs group-hover:translate-x-1 transition-transform"/>
                    </Link>
                 ))}
            </div>
        )}

      </div>

      <FeedbackModal isOpen={isFeedbackOpen} onClose={handleCloseFeedback} onSubmit={handleSubmitFeedback} />
      <ToastContainer position="bottom-center" autoClose={2000} hideProgressBar theme="colored" />
    </div>
  );
};

export default DashBoard;